import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  DatePicker,
  Button,
  Select,
  Row,
  Col,
  Spin,
  message,
  Space,
  Modal,
} from 'antd';
import { SearchOutlined, FilePdfOutlined } from '@ant-design/icons';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar } from 'recharts';
import api from '../services/api';
import dayjs from 'dayjs';
import { useAuth } from '../contexts/AuthContext';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import * as XLSX from 'xlsx';
import { departamentos, provincias, distritos } from '../data/peru';

const { RangePicker } = DatePicker;

const Reportes = () => {
  const { user } = useAuth();
  const isMainAdmin = user && user.rol === 'admin' && !user.empresa_id;

  const [loading, setLoading] = useState(false);
  const [tipoReporte, setTipoReporte] = useState('servicios');
  const [empresaId, setEmpresaId] = useState(isMainAdmin ? 'all' : (user?.empresa_id || ''));
  const [empresas, setEmpresas] = useState([]);
  const [fechaInicio, setFechaInicio] = useState(dayjs().startOf('month'));
  const [fechaFin, setFechaFin] = useState(dayjs().endOf('month'));
  const [datosServicios, setDatosServicios] = useState([]);
  const [datosIngresos, setDatosIngresos] = useState([]);
  const [datosTecnicos, setDatosTecnicos] = useState([]);
  const [datosAseguradora, setDatosAseguradora] = useState([]);
  const [datosUnidades, setDatosUnidades] = useState([]);
  const [datosUnidadesAseguradora, setDatosUnidadesAseguradora] = useState([]);
  const [datosPersonalAseguradora, setDatosPersonalAseguradora] = useState([]);

  // Estados para Modal de Detalle
  const [modalDetalleVisible, setModalDetalleVisible] = useState(false);
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const [datosDetalle, setDatosDetalle] = useState([]);
  const [tituloDetalle, setTituloDetalle] = useState('');
  const [tipoDetalleActual, setTipoDetalleActual] = useState(null);
  const [registroDetalleActual, setRegistroDetalleActual] = useState(null);

  useEffect(() => {
    if (isMainAdmin) {
      fetchEmpresas();
    }
  }, [isMainAdmin]);

  useEffect(() => {
    cargarReporte();
  }, [tipoReporte, empresaId, fechaInicio, fechaFin]);

  const fetchEmpresas = async () => {
    try {
      const response = await api.get('/empresas');
      setEmpresas(response.data || []);
    } catch (error) {
      console.error('Error al cargar empresas:', error);
    }
  };

  const cargarReporte = async () => {
    setLoading(true);
    try {
      const fechaInicioStr = fechaInicio.format('YYYY-MM-DD');
      const fechaFinStr = fechaFin.format('YYYY-MM-DD');
      const empresaParam = empresaId && empresaId !== 'all' ? `&empresa_id=${empresaId}` : '';

      switch (tipoReporte) {
        case 'servicios':
          const responseServicios = await api.get(
            `/reportes?tipo=servicios&fecha_inicio=${fechaInicioStr}&fecha_fin=${fechaFinStr}${empresaParam}`
          );
          setDatosServicios(responseServicios.data);
          break;
        case 'ingresos':
          const responseIngresos = await api.get(
            `/reportes?tipo=ingresos&fecha_inicio=${fechaInicioStr}&fecha_fin=${fechaFinStr}${empresaParam}`
          );
          setDatosIngresos(responseIngresos.data);
          break;
        case 'personal':
          const responsePersonal = await api.get(
            `/reportes?tipo=personal&fecha_inicio=${fechaInicioStr}&fecha_fin=${fechaFinStr}${empresaParam}`
          );
          setDatosTecnicos(responsePersonal.data);
          break;
        case 'aseguradora':
          const responseAseguradora = await api.get(
            `/reportes?tipo=aseguradora&fecha_inicio=${fechaInicioStr}&fecha_fin=${fechaFinStr}${empresaParam}`
          );
          setDatosAseguradora(responseAseguradora.data);
          break;
        case 'unidades':
          const responseUnidades = await api.get(
            `/reportes?tipo=unidades&fecha_inicio=${fechaInicioStr}&fecha_fin=${fechaFinStr}${empresaParam}`
          );
          setDatosUnidades(responseUnidades.data);
          break;
        case 'unidades_aseguradora':
          const responseUnidadesAseguradora = await api.get(
            `/reportes?tipo=unidades_aseguradora&fecha_inicio=${fechaInicioStr}&fecha_fin=${fechaFinStr}${empresaParam}`
          );
          setDatosUnidadesAseguradora(responseUnidadesAseguradora.data);
          break;
        case 'personal_aseguradora':
          const responsePersonalAseguradora = await api.get(
            `/reportes?tipo=personal_aseguradora&fecha_inicio=${fechaInicioStr}&fecha_fin=${fechaFinStr}${empresaParam}`
          );
          setDatosPersonalAseguradora(responsePersonalAseguradora.data);
          break;
      }
    } catch (error) {
      message.error('Error al cargar reporte');
    } finally {
      setLoading(false);
    }
  };

  const verDetalle = async (record, tipo) => {
    setLoadingDetalle(true);
    setModalDetalleVisible(true);
    setDatosDetalle([]);

    let titulo = 'Detalle de Servicios';
    let params = `fecha_inicio=${fechaInicio.format('YYYY-MM-DD')}&fecha_fin=${fechaFin.format('YYYY-MM-DD')}`;

    if (empresaId && empresaId !== 'all') {
      params += `&empresa_id=${empresaId}`;
    }

    setTipoDetalleActual(tipo);
    setRegistroDetalleActual(record);

    if (tipo === 'unidad_aseguradora') {
      titulo = `Servicios de ${record.placa} - ${record.aseguradora_nombre}`;
      params += `&unidad_id=${record.unidad_id}&aseguradora_id=${record.aseguradora_id}`;
    } else if (tipo === 'personal_aseguradora') {
      titulo = `Servicios de ${record.nombre_completo} - ${record.aseguradora_nombre}`;
      params += `&personal_id=${record.personal_id}&aseguradora_id=${record.aseguradora_id}`;
    } else if (tipo === 'personal') {
      titulo = `Servicios de ${record.nombre_completo}`;
      // Asegurar que tenemos el ID correcto, probando personal_id o id
      const idPersonal = record.personal_id || record.id;
      if (!idPersonal) {
        console.error('No se encontró ID para el personal', record);
        message.error('Error al identificar al personal');
        return;
      }
      params += `&personal_id=${idPersonal}`;
    } else if (tipo === 'ingresos') {
      const fechaStr = dayjs(record.fecha).format('YYYY-MM-DD');
      titulo = `Servicios del ${dayjs(record.fecha).format('DD/MM/YYYY')}`;
      // Sobrescribir fechas para filtrar solo ese día
      params = `fecha_inicio=${fechaStr}&fecha_fin=${fechaStr}`;
      if (empresaId && empresaId !== 'all') {
        params += `&empresa_id=${empresaId}`;
      }
    } else if (tipo === 'aseguradora') {
      titulo = `Servicios de ${record.aseguradora_nombre}`;
      params += `&aseguradora_id=${record.id}`;
    } else if (tipo === 'unidades') {
      titulo = `Servicios de la Unidad ${record.placa}`;
      params += `&unidad_id=${record.id}`;
    }

    setTituloDetalle(titulo);

    try {
      const response = await api.get(`/reportes?tipo=detalle&${params}`);
      setDatosDetalle(response.data);
    } catch (error) {
      console.error('Error al cargar detalle:', error);
      message.error('Error al cargar detalle de servicios');
    } finally {
      setLoadingDetalle(false);
    }
  };

  const getUbicacionNombre = (tipo, id, parentId = null) => {
    if (!id) return '-';
    try {
      if (tipo === 'departamento') {
        const dep = departamentos.find(d => d.id === id);
        return dep ? dep.nombre : id;
      }
      if (tipo === 'provincia' && parentId) {
        const provs = provincias[parentId] || [];
        const prov = provs.find(p => p.id === id);
        return prov ? prov.nombre : id;
      }
      if (tipo === 'distrito' && parentId) {
        // El parentId para distrito es la provincia
        // A veces distritos no está exportado directamente o tiene otra estructura, verificar data/peru.js
        // Si distritos es importado correctamente:
        const dists = distritos[parentId] || [];
        const dist = dists.find(d => d.id === id);
        return dist ? dist.nombre : id;
      }
    } catch (e) {
      console.error('Error buscando ubicación', e);
      return id;
    }
    return id;
  };

  const handleExportarDetalleExcel = () => {
    try {
      if (!datosDetalle || datosDetalle.length === 0) {
        message.warning('No hay datos para exportar');
        return;
      }

      // Estandarización: Usar el mismo formato para TODOS los reportes
      const datosOrdenados = [...datosDetalle].sort((a, b) => {
        return new Date(a.fecha_solicitud) - new Date(b.fecha_solicitud);
      });

      const dataToExport = datosOrdenados.map(item => {
        const incluyeIgv = item.incluye_igv !== 0 && item.incluye_igv !== false && item.incluye_igv !== '0';
        const costoTotal = parseFloat(item.costo || 0);
        let costoSinIgv = costoTotal;
        if (incluyeIgv) {
          costoSinIgv = costoTotal / 1.18;
        }

        return {
          'Fecha': dayjs(item.fecha_solicitud).format('DD/MM/YYYY'),
          'Compañía': item.aseguradora_nombre || '-',
          'Expediente': item.numero_expediente || '-',
          'Tipo de Servicio': (item.tipo_servicio_codigo || item.tipo_servicio) || '-',
          'Estado de Servicio': item.estado || '-',
          'Departamento Inicio': getUbicacionNombre('departamento', item.departamento_contacto),
          'Provincia Inicio': getUbicacionNombre('provincia', item.provincia_contacto, item.departamento_contacto),
          'Distrito Inicio': getUbicacionNombre('distrito', item.distrito_contacto, item.provincia_contacto),
          'Dirección Inicio': item.lugar_contacto || '-',
          'Departamento Destino': getUbicacionNombre('departamento', item.departamento_destino),
          'Provincia Destino': getUbicacionNombre('provincia', item.provincia_destino, item.departamento_destino),
          'Distrito Destino': getUbicacionNombre('distrito', item.distrito_destino, item.provincia_destino),
          'Dirección Destino': item.lugar_destino || '-',
          'Tipo de Vehículo': item.tipo_vehiculo || '-',
          'Marca de Vehículo': item.marca_vehiculo || '-',
          'Placa': item.placa_vehiculo || '-',
          'Nombre Operador': item.personal_nombre || '-',
          'Unidad': item.unidad_placa || '-',
          'Monto Sin IGV': parseFloat(costoSinIgv.toFixed(2)),
          'Monto Total Con IGV': parseFloat(costoTotal.toFixed(2))
        };
      });

      const ws = XLSX.utils.json_to_sheet(dataToExport);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Detalle Servicios");
      // Limpiar nombre de archivo de caracteres inválidos
      const cleanTitle = tituloDetalle.replace(/[^a-zA-Z0-9]/g, '_');
      const fileName = `Detalle_${cleanTitle}_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`;
      XLSX.writeFile(wb, fileName);

      message.success('Excel exportado correctamente');
    } catch (error) {
      console.error('Error al exportar detalle excel:', error);
      message.error('Error al exportar Excel');
    }
  };

  const handleExportarDetallePDF2 = async () => {
    try {
      message.loading({ content: 'Generando PDF...', key: 'export_detalle' });

      const isPersonalReport = tipoDetalleActual === 'personal';
      // Use landscape for personal report due to many columns
      const orientation = isPersonalReport ? 'landscape' : 'portrait';
      const widthMm = isPersonalReport ? 297 : 210;

      const div = document.createElement('div');
      div.style.position = 'absolute';
      div.style.left = '-9999px';
      div.style.width = `${widthMm}mm`;
      div.style.padding = '10px';
      div.style.fontFamily = 'Arial, sans-serif';
      div.style.fontSize = isPersonalReport ? '7px' : '10px'; // Smaller font for personal report
      div.style.backgroundColor = 'white';

      let html = '';

      if (isPersonalReport) {
        // Lógica específica para reporte de personal
        html = `
            <div style="text-align: center; margin-bottom: 20px;">
              <h2 style="margin: 0; font-size: 14px;">REPORTE DE SERVICIOS POR PERSONAL</h2>
              <div style="margin-top: 5px; font-size: 10px; text-align: left;">
                <p><strong>NOMBRES:</strong> ${registroDetalleActual?.nombre_completo || '-'}</p>
                <p><strong>RANGO DE FECHAS DE REPORTE:</strong> ${fechaInicio.format('DD/MM/YYYY')} - ${fechaFin.format('DD/MM/YYYY')}</p>
              </div>
            </div>
          `;

        // Agrupar por aseguradora
        const porAseguradora = {};
        let totalGeneralServicios = 0;
        let totalGeneralMonto = 0;

        datosDetalle.forEach(item => {
          const aseguradora = item.aseguradora_nombre || 'Sin Aseguradora';
          if (!porAseguradora[aseguradora]) {
            porAseguradora[aseguradora] = {
              servicios: [],
              totalMonto: 0,
              count: 0
            };
          }
          porAseguradora[aseguradora].servicios.push(item);
          const costo = parseFloat(item.costo || 0);
          porAseguradora[aseguradora].totalMonto += costo;
          porAseguradora[aseguradora].count += 1;

          totalGeneralServicios += 1;
          totalGeneralMonto += costo;
        });

        // Tabla resumen
        html += `
            <h3 style="font-size: 12px; margin-top: 10px;">RESUMEN POR EMPRESA</h3>
            <table style="width: 100%; border-collapse: collapse; margin-bottom: 15px; font-size: 9px;">
              <thead>
                <tr style="background-color: #f0f0f0;">
                  <th style="border: 1px solid #ddd; padding: 4px; text-align: left;">EMPRESA</th>
                  <th style="border: 1px solid #ddd; padding: 4px; text-align: right;">TOTAL SERVICIOS</th>
                  <th style="border: 1px solid #ddd; padding: 4px; text-align: right;">MONTO FACTURADO</th>
                </tr>
              </thead>
              <tbody>
          `;

        Object.keys(porAseguradora).forEach(aseg => {
          html += `
                <tr>
                  <td style="border: 1px solid #ddd; padding: 4px;">${aseg}</td>
                  <td style="border: 1px solid #ddd; padding: 4px; text-align: right;">${porAseguradora[aseg].count}</td>
                  <td style="border: 1px solid #ddd; padding: 4px; text-align: right;">S/ ${porAseguradora[aseg].totalMonto.toFixed(2)}</td>
                </tr>
              `;
        });

        html += `
                <tr style="font-weight: bold; background-color: #e6f7ff;">
                  <td style="border: 1px solid #ddd; padding: 4px;">TOTALES GENERALES</td>
                  <td style="border: 1px solid #ddd; padding: 4px; text-align: right;">${totalGeneralServicios}</td>
                  <td style="border: 1px solid #ddd; padding: 4px; text-align: right;">S/ ${totalGeneralMonto.toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
          `;

        // Listado detallado agrupado
        html += `<h3 style="font-size: 12px; margin-top: 15px;">LISTADO DE SERVICIOS</h3>`;

        Object.keys(porAseguradora).sort().forEach(aseg => {
          html += `<div style="margin-top: 10px; font-weight: bold; font-size: 10px; background: #eee; padding: 4px;">ASEGURADORA: ${aseg}</div>`;

          html += `
                <table style="width: 100%; border-collapse: collapse; font-size: 6px; table-layout: fixed;">
                  <thead>
                    <tr style="background-color: #f9f9f9;">
                      <th style="border: 1px solid #ddd; padding: 2px; width: 40px;">FECHA</th>
                      <th style="border: 1px solid #ddd; padding: 2px; width: 40px;">EXP.</th>
                      <th style="border: 1px solid #ddd; padding: 2px; width: 50px;">SERVICIO</th>
                      <th style="border: 1px solid #ddd; padding: 2px; width: 35px;">ESTADO</th>
                      <th style="border: 1px solid #ddd; padding: 2px;">DEP. INI</th>
                      <th style="border: 1px solid #ddd; padding: 2px;">PROV. INI</th>
                      <th style="border: 1px solid #ddd; padding: 2px;">DIST. INI</th>
                      <th style="border: 1px solid #ddd; padding: 2px;">DIR. INI</th>
                      <th style="border: 1px solid #ddd; padding: 2px;">DEP. DES</th>
                      <th style="border: 1px solid #ddd; padding: 2px;">PROV. DES</th>
                      <th style="border: 1px solid #ddd; padding: 2px;">DIST. DES</th>
                      <th style="border: 1px solid #ddd; padding: 2px;">DIR. DES</th>
                      <th style="border: 1px solid #ddd; padding: 2px;">TIPO VEH</th>
                      <th style="border: 1px solid #ddd; padding: 2px;">MARCA</th>
                      <th style="border: 1px solid #ddd; padding: 2px;">PLACA</th>
                      <th style="border: 1px solid #ddd; padding: 2px;">OPERADOR</th>
                      <th style="border: 1px solid #ddd; padding: 2px;">UNIDAD</th>
                      <th style="border: 1px solid #ddd; padding: 2px; text-align: right; width: 30px;">S/ s/IGV</th>
                      <th style="border: 1px solid #ddd; padding: 2px; text-align: right; width: 30px;">S/ Total</th>
                    </tr>
                  </thead>
                  <tbody>
              `;

          const serviciosOrdenados = porAseguradora[aseg].servicios.sort((a, b) => new Date(a.fecha_solicitud) - new Date(b.fecha_solicitud));

          serviciosOrdenados.forEach((item, idx) => {
            const incluyeIgv = item.incluye_igv !== 0 && item.incluye_igv !== false;
            const costoTotal = parseFloat(item.costo || 0);
            let costoSinIgv = costoTotal;
            if (incluyeIgv) {
              costoSinIgv = costoTotal / 1.18;
            }
            const origen = [
              getUbicacionNombre('distrito', item.distrito_contacto, item.provincia_contacto),
              item.lugar_contacto
            ].filter(val => val && val !== '-').join(' - ');

            const destino = [
              getUbicacionNombre('distrito', item.distrito_destino, item.provincia_destino),
              item.lugar_destino
            ].filter(val => val && val !== '-').join(' - ');

            const vehiculo = [item.marca_vehiculo, item.placa_vehiculo].filter(Boolean).join(' ');

            html += `
                    <tr>
                      <td style="border: 1px solid #ddd; padding: 2px;">${dayjs(item.fecha_solicitud).format('DD/MM/YY')}</td>
                      <td style="border: 1px solid #ddd; padding: 2px; word-break: break-all;">${item.numero_expediente || '-'}</td>
                      <td style="border: 1px solid #ddd; padding: 2px; word-break: break-all;">${item.tipo_servicio_codigo || item.tipo_servicio || '-'}</td>
                      <td style="border: 1px solid #ddd; padding: 2px;">${item.estado || '-'}</td>
                      <td style="border: 1px solid #ddd; padding: 2px;">${getUbicacionNombre('departamento', item.departamento_contacto)}</td>
                      <td style="border: 1px solid #ddd; padding: 2px;">${getUbicacionNombre('provincia', item.provincia_contacto, item.departamento_contacto)}</td>
                      <td style="border: 1px solid #ddd; padding: 2px;">${getUbicacionNombre('distrito', item.distrito_contacto, item.provincia_contacto)}</td>
                      <td style="border: 1px solid #ddd; padding: 2px; word-break: break-all;">${item.lugar_contacto || '-'}</td>
                      <td style="border: 1px solid #ddd; padding: 2px;">${getUbicacionNombre('departamento', item.departamento_destino)}</td>
                      <td style="border: 1px solid #ddd; padding: 2px;">${getUbicacionNombre('provincia', item.provincia_destino, item.departamento_destino)}</td>
                      <td style="border: 1px solid #ddd; padding: 2px;">${getUbicacionNombre('distrito', item.distrito_destino, item.provincia_destino)}</td>
                      <td style="border: 1px solid #ddd; padding: 2px; word-break: break-all;">${item.lugar_destino || '-'}</td>
                      <td style="border: 1px solid #ddd; padding: 2px;">${item.tipo_vehiculo || '-'}</td>
                      <td style="border: 1px solid #ddd; padding: 2px;">${item.marca_vehiculo || '-'}</td>
                      <td style="border: 1px solid #ddd; padding: 2px;">${item.placa_vehiculo || '-'}</td>
                      <td style="border: 1px solid #ddd; padding: 2px;">${item.personal_nombre || '-'}</td>
                      <td style="border: 1px solid #ddd; padding: 2px;">${item.unidad_placa || '-'}</td>
                      <td style="border: 1px solid #ddd; padding: 2px; text-align: right;">${costoSinIgv.toFixed(2)}</td>
                      <td style="border: 1px solid #ddd; padding: 2px; text-align: right;">${costoTotal.toFixed(2)}</td>
                    </tr>
                  `;
          });

          html += `</tbody></table>`;
        });
      } else {
        // Formato estándar para otros reportes
        html = `
        <div style="text-align: center; margin-bottom: 20px;">
          <h2 style="margin: 0; font-size: 18px;">${tituloDetalle}</h2>
          <p style="margin: 5px 0; font-size: 10px; color: #999;">
            Generado el: ${dayjs().format('DD/MM/YYYY HH:mm:ss')}
          </p>
        </div>
        <table style="width: 100%; border-collapse: collapse; margin-top: 20px;">
          <thead>
            <tr style="background-color: #f0f0f0;">
              <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Fecha</th>
              <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Cliente</th>
              <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Vehículo</th>
              <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Servicio</th>
              <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Personal</th>
              <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Unidad</th>
              <th style="border: 1px solid #ddd; padding: 8px; text-align: right;">Costo</th>
            </tr>
          </thead>
          <tbody>
      `;

        let totalCosto = 0;
        datosDetalle.forEach((item, index) => {
          totalCosto += parseFloat(item.costo || 0);
          html += `
          <tr style="background-color: ${index % 2 === 0 ? 'white' : '#f9f9f9'};">
            <td style="border: 1px solid #ddd; padding: 6px;">${item.fecha_solicitud ? dayjs(item.fecha_solicitud).format('DD/MM/YYYY HH:mm') : '-'}</td>
            <td style="border: 1px solid #ddd; padding: 6px;">${`${item.cliente_nombre || ''} ${item.cliente_apellido || ''}`.trim()}</td>
            <td style="border: 1px solid #ddd; padding: 6px;">${`${item.marca_vehiculo || ''} ${item.modelo_vehiculo || ''} ${item.placa_vehiculo || ''}`.trim()}</td>
            <td style="border: 1px solid #ddd; padding: 6px;">${item.tipo_servicio_codigo ? `${item.tipo_servicio_codigo} - ${item.tipo_servicio}` : item.tipo_servicio}</td>
             <td style="border: 1px solid #ddd; padding: 6px;">${item.personal_nombre || '-'}</td>
            <td style="border: 1px solid #ddd; padding: 6px;">${item.unidad_placa || '-'}</td>
            <td style="border: 1px solid #ddd; padding: 6px; text-align: right;">S/ ${parseFloat(item.costo || 0).toFixed(2)}</td>
          </tr>
        `;
        });

        html += `
          </tbody>
          <tfoot>
             <tr style="background-color: #e6f7ff; font-weight: bold;">
               <td colspan="6" style="border: 1px solid #ddd; padding: 8px; text-align: right;">TOTAL:</td>
               <td style="border: 1px solid #ddd; padding: 8px; text-align: right;">S/ ${totalCosto.toFixed(2)}</td>
             </tr>
          </tfoot>
        </table>
      `;
      }

      div.innerHTML = html;
      document.body.appendChild(div);

      // Wait a bit for render
      await new Promise(resolve => setTimeout(resolve, 500));

      const canvas = await html2canvas(div, {
        scale: 2,
        useCORS: true,
        logging: false,
        windowWidth: div.scrollWidth,
        windowHeight: div.scrollHeight
      });

      document.body.removeChild(div);

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF(orientation === 'landscape' ? 'l' : 'p', 'mm', 'a4');
      const imgWidth = widthMm;
      const pageHeight = orientation === 'landscape' ? 210 : 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, -pageHeight + (imgHeight - heightLeft), imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      const fileName = `Detalle_${tituloDetalle.replace(/\s+/g, '_')}_${dayjs().format('YYYYMMDD_HHmmss')}.pdf`;
      pdf.save(fileName);

      message.success({ content: 'PDF generado exitosamente', key: 'export_detalle' });

    } catch (error) {
      console.error('Error exportando PDF detalle', error);
      message.error({ content: 'Error al generar PDF', key: 'export_detalle' });
    }
  };

  const columnsServicios = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: 'Cliente',
      key: 'cliente',
      render: (_, record) =>
        `${record.cliente_nombre} ${record.cliente_apellido}`,
    },
    {
      title: 'Vehículo',
      key: 'vehiculo',
      render: (_, record) => {
        const marca = record.marca || '';
        const modelo = record.modelo || '';
        const placa = record.placa || '';
        const vehiculoStr = [marca, modelo, placa].filter(Boolean).join(' ');
        return vehiculoStr || '-';
      },
    },
    {
      title: 'Tipo de Servicio',
      key: 'tipo_servicio',
      render: (_, record) => {
        if (record.tipo_servicio_codigo && record.tipo_servicio) {
          return `${record.tipo_servicio_codigo} - ${record.tipo_servicio}`;
        }
        return record.tipo_servicio || '-';
      },
    },
    {
      title: 'Estado',
      dataIndex: 'estado',
      key: 'estado',
    },
    {
      title: 'Costo',
      dataIndex: 'costo',
      key: 'costo',
      render: (costo) => `S/ ${costo}`,
    },
    {
      title: 'Fecha',
      dataIndex: 'fecha_solicitud',
      key: 'fecha',
    },
  ];

  const columnsIngresos = [
    {
      title: 'Fecha',
      dataIndex: 'fecha',
      key: 'fecha',
      render: (fecha) => dayjs(fecha).format('DD/MM/YYYY'),
      sorter: (a, b) => new Date(a.fecha) - new Date(b.fecha),
    },
    {
      title: 'Total Ingresos',
      dataIndex: 'total',
      key: 'total',
      render: (total) => `S/ ${parseFloat(total || 0).toFixed(2)}`,
      sorter: (a, b) => a.total - b.total,
    },
    {
      title: 'Acciones',
      key: 'acciones',
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          onClick={() => verDetalle(record, 'ingresos')}
        >
          Ver Detalle
        </Button>
      ),
    },
  ];

  const columnsTecnicos = [
    {
      title: 'Personal',
      key: 'personal',
      render: (_, record) => record.nombre_completo || '-',
    },
    {
      title: 'Cargo',
      dataIndex: 'cargo',
      key: 'cargo',
    },
    {
      title: 'Total Servicios',
      dataIndex: 'total_servicios',
      key: 'total_servicios',
    },
    {
      title: 'Total Ingresos',
      dataIndex: 'total_ingresos',
      key: 'total_ingresos',
      render: (ingresos) => `S/ ${parseFloat(ingresos || 0).toFixed(2)}`,
    },
    {
      title: 'Acciones',
      key: 'acciones',
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          onClick={() => verDetalle(record, 'personal')}
        >
          Ver Detalle
        </Button>
      ),
    },
  ];

  const columnsAseguradora = [
    {
      title: 'Aseguradora',
      dataIndex: 'aseguradora_nombre',
      key: 'aseguradora_nombre',
    },
    {
      title: 'Total Servicios',
      dataIndex: 'total_servicios',
      key: 'total_servicios',
    },
    {
      title: 'Total Ingresos',
      dataIndex: 'total_ingresos',
      key: 'total_ingresos',
      render: (ingresos) => `S/ ${parseFloat(ingresos || 0).toFixed(2)}`,
    },
    {
      title: 'Total Clientes',
      dataIndex: 'total_clientes',
      key: 'total_clientes',
    },
    {
      title: 'Acciones',
      key: 'acciones',
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          onClick={() => verDetalle(record, 'aseguradora')}
        >
          Ver Detalle
        </Button>
      ),
    },
  ];

  const columnsUnidades = [
    {
      title: 'Placa',
      dataIndex: 'placa',
      key: 'placa',
    },
    {
      title: 'Tipo de Grúa',
      dataIndex: 'tipo_grua',
      key: 'tipo_grua',
    },
    {
      title: 'Marca/Modelo',
      key: 'marca_modelo',
      render: (_, record) => {
        const marca = record.marca || '';
        const modelo = record.modelo || '';
        return [marca, modelo].filter(Boolean).join(' ') || '-';
      },
    },
    {
      title: 'Total Servicios',
      dataIndex: 'total_servicios',
      key: 'total_servicios',
    },
    {
      title: 'Total Ingresos',
      dataIndex: 'total_ingresos',
      key: 'total_ingresos',
      render: (ingresos) => `S/ ${parseFloat(ingresos || 0).toFixed(2)}`,
    },
    {
      title: 'Promedio Costo',
      dataIndex: 'promedio_costo',
      key: 'promedio_costo',
      render: (costo) => `S/ ${parseFloat(costo || 0).toFixed(2)}`,
    },
    {
      title: 'Total Operarios',
      dataIndex: 'total_operarios',
      key: 'total_operarios',
    },
    {
      title: 'Acciones',
      key: 'acciones',
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          onClick={() => verDetalle(record, 'unidades')}
        >
          Ver Detalle
        </Button>
      ),
    },
  ];

  const columnsUnidadesAseguradora = [
    {
      title: 'Placa',
      dataIndex: 'placa',
      key: 'placa',
    },
    {
      title: 'Tipo de Grúa',
      dataIndex: 'tipo_grua',
      key: 'tipo_grua',
    },
    {
      title: 'Aseguradora',
      dataIndex: 'aseguradora_nombre',
      key: 'aseguradora_nombre',
    },
    {
      title: 'Total Servicios',
      dataIndex: 'total_servicios',
      key: 'total_servicios',
      sorter: (a, b) => a.total_servicios - b.total_servicios,
    },
    {
      title: 'Total Ingresos',
      dataIndex: 'total_ingresos',
      key: 'total_ingresos',
      render: (ingresos) => `S/ ${parseFloat(ingresos || 0).toFixed(2)}`,
      sorter: (a, b) => a.total_ingresos - b.total_ingresos,
    },
    {
      title: 'Acciones',
      key: 'acciones',
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          onClick={() => verDetalle(record, 'unidad_aseguradora')}
        >
          Ver Detalle
        </Button>
      ),
    },
  ];

  const columnsPersonalAseguradora = [
    {
      title: 'Personal',
      dataIndex: 'nombre_completo',
      key: 'nombre_completo',
    },
    {
      title: 'Cargo',
      dataIndex: 'cargo',
      key: 'cargo',
    },
    {
      title: 'Aseguradora',
      dataIndex: 'aseguradora_nombre',
      key: 'aseguradora_nombre',
    },
    {
      title: 'Total Servicios',
      dataIndex: 'total_servicios',
      key: 'total_servicios',
      sorter: (a, b) => a.total_servicios - b.total_servicios,
    },
    {
      title: 'Total Ingresos',
      dataIndex: 'total_ingresos',
      key: 'total_ingresos',
      render: (ingresos) => `S/ ${parseFloat(ingresos || 0).toFixed(2)}`,
      sorter: (a, b) => a.total_ingresos - b.total_ingresos,
    },
    {
      title: 'Acciones',
      key: 'acciones',
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          onClick={() => verDetalle(record, 'personal_aseguradora')}
        >
          Ver Detalle
        </Button>
      ),
    },
  ];

  const getTipoReporteNombre = () => {
    const nombres = {
      servicios: 'Servicios por Fecha',
      ingresos: 'Ingresos por Fecha',
      personal: 'Servicios por Personal',
      aseguradora: 'Servicios por Aseguradora',
      unidades: 'Servicios por Unidades',
      unidades_aseguradora: 'Unidades por Aseguradora',
      personal_aseguradora: 'Personal por Aseguradora',
    };
    return nombres[tipoReporte] || 'Reporte';
  };

  const getEmpresaNombre = () => {
    if (empresaId === 'all' || !empresaId) {
      return 'Todas las empresas';
    }
    const empresa = empresas.find((e) => e.id === parseInt(empresaId));
    return empresa ? empresa.nombre : 'Empresa';
  };

  const handleExportarExcel = () => {
    try {
      message.loading({ content: 'Generando Excel...', key: 'export' });

      let dataToExport = [];
      let fileName = `Reporte_${getTipoReporteNombre().replace(/\s+/g, '_')}_${dayjs().format('YYYYMMDD_HHmmss')}`;

      switch (tipoReporte) {
        case 'servicios':
          // Ordenar por fecha
          const serviciosOrdenados = [...datosServicios].sort((a, b) => new Date(a.fecha_solicitud) - new Date(b.fecha_solicitud));

          dataToExport = serviciosOrdenados.map(item => {
            const incluyeIgv = item.incluye_igv !== 0 && item.incluye_igv !== false && item.incluye_igv !== '0';
            const costoTotal = parseFloat(item.costo || 0);
            let costoSinIgv = costoTotal;
            if (incluyeIgv) {
              costoSinIgv = costoTotal / 1.18;
            }

            return {
              'Fecha': dayjs(item.fecha_solicitud).format('DD/MM/YYYY'),
              'Compañía': item.aseguradora_nombre || '-',
              'Expediente': item.numero_expediente || '-',
              'Tipo de Servicio': (item.tipo_servicio_codigo || item.tipo_servicio) || '-',
              'Estado de Servicio': item.estado || '-',
              'Departamento Inicio': getUbicacionNombre('departamento', item.departamento_contacto),
              'Provincia Inicio': getUbicacionNombre('provincia', item.provincia_contacto, item.departamento_contacto),
              'Distrito Inicio': getUbicacionNombre('distrito', item.distrito_contacto, item.provincia_contacto),
              'Dirección Inicio': item.lugar_contacto || '-',
              'Departamento Destino': getUbicacionNombre('departamento', item.departamento_destino),
              'Provincia Destino': getUbicacionNombre('provincia', item.provincia_destino, item.departamento_destino),
              'Distrito Destino': getUbicacionNombre('distrito', item.distrito_destino, item.provincia_destino),
              'Dirección Destino': item.lugar_destino || '-',
              'Tipo de Vehículo': item.tipo_vehiculo || '-',
              'Marca de Vehículo': item.marca || '-',
              'Placa': item.placa || '-',
              'Nombre Operador': item.trabajador_nombre || '-',
              'Unidad': item.unidad_placa || '-',
              'Monto Sin IGV': parseFloat(costoSinIgv.toFixed(2)),
              'Monto Total Con IGV': parseFloat(costoTotal.toFixed(2))
            };
          });
          break;
        case 'ingresos':
          dataToExport = datosIngresos.map(item => ({
            Fecha: item.fecha,
            'Total Ingresos': parseFloat(item.total || 0)
          }));
          break;
        case 'personal':
          dataToExport = datosTecnicos.map(item => ({
            Personal: item.nombre_completo,
            Cargo: item.cargo,
            'Total Servicios': parseInt(item.total_servicios || 0),
            'Total Ingresos': parseFloat(item.total_ingresos || 0)
          }));
          break;
        case 'aseguradora':
          dataToExport = datosAseguradora.map(item => ({
            Aseguradora: item.aseguradora_nombre,
            'Total Servicios': parseInt(item.total_servicios || 0),
            'Total Ingresos': parseFloat(item.total_ingresos || 0),
            'Total Clientes': parseInt(item.total_clientes || 0)
          }));
          break;
        case 'unidades':
          dataToExport = datosUnidades.map(item => ({
            Placa: item.placa,
            'Tipo de Grúa': item.tipo_grua,
            'Marca/Modelo': [item.marca, item.modelo].filter(Boolean).join(' '),
            'Total Servicios': parseInt(item.total_servicios || 0),
            'Total Ingresos': parseFloat(item.total_ingresos || 0),
            'Promedio Costo': parseFloat(item.promedio_costo || 0),
            'Total Operarios': parseInt(item.total_operarios || 0)
          }));
          break;
        case 'unidades_aseguradora':
          dataToExport = datosUnidadesAseguradora.map(item => ({
            Placa: item.placa,
            'Tipo de Grúa': item.tipo_grua,
            Aseguradora: item.aseguradora_nombre,
            'Total Servicios': parseInt(item.total_servicios || 0),
            'Total Ingresos': parseFloat(item.total_ingresos || 0)
          }));
          break;
        case 'personal_aseguradora':
          dataToExport = datosPersonalAseguradora.map(item => ({
            Personal: item.nombre_completo,
            Cargo: item.cargo,
            Aseguradora: item.aseguradora_nombre,
            'Total Servicios': parseInt(item.total_servicios || 0),
            'Total Ingresos': parseFloat(item.total_ingresos || 0)
          }));
          break;
        default:
          dataToExport = [];
      }

      if (dataToExport.length === 0) {
        message.warning({ content: 'No hay datos para exportar', key: 'export' });
        return;
      }

      const ws = XLSX.utils.json_to_sheet(dataToExport);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Reporte");
      XLSX.writeFile(wb, `${fileName}.xlsx`);

      message.success({ content: 'Excel generado exitosamente', key: 'export' });
    } catch (error) {
      console.error('Error al exportar Excel:', error);
      message.error({ content: 'Error al exportar Excel', key: 'export' });
    }
  };

  const handleExportarPDF = async () => {
    try {
      message.loading({ content: 'Generando PDF...', key: 'export' });

      const div = document.createElement('div');
      div.style.position = 'absolute';
      div.style.left = '-9999px';
      div.style.width = '210mm';
      div.style.padding = '20px';
      div.style.fontFamily = 'Arial, sans-serif';
      div.style.fontSize = '12px';

      let html = `
        <div style="text-align: center; margin-bottom: 20px;">
          <h2 style="margin: 0; font-size: 18px;">${getTipoReporteNombre()}</h2>
          <p style="margin: 5px 0; font-size: 12px; color: #666;">
            Período: ${fechaInicio.format('DD/MM/YYYY')} - ${fechaFin.format('DD/MM/YYYY')}
          </p>
          ${isMainAdmin ? `<p style="margin: 5px 0; font-size: 12px; color: #666;">Empresa: ${getEmpresaNombre()}</p>` : ''}
          <p style="margin: 5px 0; font-size: 10px; color: #999;">
            Generado el: ${dayjs().format('DD/MM/YYYY HH:mm:ss')}
          </p>
        </div>
        <table style="width: 100%; border-collapse: collapse; margin-top: 20px;">
      `;

      switch (tipoReporte) {
        case 'servicios':
          if (!datosServicios || datosServicios.length === 0) {
            html += '<tr><td colspan="7" style="text-align: center; padding: 20px;">No hay datos para mostrar</td></tr>';
          } else {
            html += `
              <thead>
                <tr style="background-color: #f0f0f0;">
                  <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">ID</th>
                  <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Cliente</th>
                  <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Vehículo</th>
                  <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Tipo de Servicio</th>
                  <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Estado</th>
                  <th style="border: 1px solid #ddd; padding: 8px; text-align: right;">Costo</th>
                  <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Fecha</th>
                </tr>
              </thead>
              <tbody>
            `;
            datosServicios.forEach((record, index) => {
              const marca = record.marca || '';
              const modelo = record.modelo || '';
              const placa = record.placa || '';
              const vehiculoStr = [marca, modelo, placa].filter(Boolean).join(' ') || '-';
              const tipoServicio = record.tipo_servicio_codigo && record.tipo_servicio
                ? `${record.tipo_servicio_codigo} - ${record.tipo_servicio}`
                : (record.tipo_servicio || '-');
              const cliente = `${record.cliente_nombre || ''} ${record.cliente_apellido || ''}`.trim() || '-';

              html += `
                <tr style="background-color: ${index % 2 === 0 ? 'white' : '#f9f9f9'};">
                  <td style="border: 1px solid #ddd; padding: 6px;">${record.id || '-'}</td>
                  <td style="border: 1px solid #ddd; padding: 6px;">${cliente}</td>
                  <td style="border: 1px solid #ddd; padding: 6px;">${vehiculoStr}</td>
                  <td style="border: 1px solid #ddd; padding: 6px;">${tipoServicio}</td>
                  <td style="border: 1px solid #ddd; padding: 6px;">${record.estado || '-'}</td>
                  <td style="border: 1px solid #ddd; padding: 6px; text-align: right;">S/ ${parseFloat(record.costo || 0).toFixed(2)}</td>
                  <td style="border: 1px solid #ddd; padding: 6px;">${record.fecha_solicitud ? dayjs(record.fecha_solicitud).format('DD/MM/YYYY') : '-'}</td>
                </tr>
              `;
            });
            html += '</tbody>';
          }
          break;

        case 'ingresos':
          if (!datosIngresos || datosIngresos.length === 0) {
            html += '<tr><td colspan="2" style="text-align: center; padding: 20px;">No hay datos para mostrar</td></tr>';
          } else {
            html += `
              <thead>
                <tr style="background-color: #f0f0f0;">
                  <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Fecha</th>
                  <th style="border: 1px solid #ddd; padding: 8px; text-align: right;">Total Ingresos</th>
                </tr>
              </thead>
              <tbody>
            `;
            let totalIngresos = 0;
            datosIngresos.forEach((record, index) => {
              const total = parseFloat(record.total || 0);
              totalIngresos += total;
              html += `
                <tr style="background-color: ${index % 2 === 0 ? 'white' : '#f9f9f9'};">
                  <td style="border: 1px solid #ddd; padding: 6px;">${record.fecha || '-'}</td>
                  <td style="border: 1px solid #ddd; padding: 6px; text-align: right;">S/ ${total.toFixed(2)}</td>
                </tr>
              `;
            });
            html += `
              </tbody>
              <tfoot>
                <tr style="background-color: #e6f7ff; font-weight: bold;">
                  <td style="border: 1px solid #ddd; padding: 8px; text-align: right;">TOTAL:</td>
                  <td style="border: 1px solid #ddd; padding: 8px; text-align: right;">S/ ${totalIngresos.toFixed(2)}</td>
                </tr>
              </tfoot>
            `;
          }
          break;

        case 'personal':
          if (!datosTecnicos || datosTecnicos.length === 0) {
            html += '<tr><td colspan="4" style="text-align: center; padding: 20px;">No hay datos para mostrar</td></tr>';
          } else {
            html += `
              <thead>
                <tr style="background-color: #f0f0f0;">
                  <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Personal</th>
                  <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Cargo</th>
                  <th style="border: 1px solid #ddd; padding: 8px; text-align: right;">Total Servicios</th>
                  <th style="border: 1px solid #ddd; padding: 8px; text-align: right;">Total Ingresos</th>
                </tr>
              </thead>
              <tbody>
            `;
            datosTecnicos.forEach((record, index) => {
              html += `
                <tr style="background-color: ${index % 2 === 0 ? 'white' : '#f9f9f9'};">
                  <td style="border: 1px solid #ddd; padding: 6px;">${record.nombre_completo || '-'}</td>
                  <td style="border: 1px solid #ddd; padding: 6px;">${record.cargo || '-'}</td>
                  <td style="border: 1px solid #ddd; padding: 6px; text-align: right;">${record.total_servicios || 0}</td>
                  <td style="border: 1px solid #ddd; padding: 6px; text-align: right;">S/ ${parseFloat(record.total_ingresos || 0).toFixed(2)}</td>
                </tr>
              `;
            });
            html += '</tbody>';
          }
          break;

        case 'aseguradora':
          if (!datosAseguradora || datosAseguradora.length === 0) {
            html += '<tr><td colspan="4" style="text-align: center; padding: 20px;">No hay datos para mostrar</td></tr>';
          } else {
            html += `
              <thead>
                <tr style="background-color: #f0f0f0;">
                  <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Aseguradora</th>
                  <th style="border: 1px solid #ddd; padding: 8px; text-align: right;">Total Servicios</th>
                  <th style="border: 1px solid #ddd; padding: 8px; text-align: right;">Total Ingresos</th>
                  <th style="border: 1px solid #ddd; padding: 8px; text-align: right;">Total Clientes</th>
                </tr>
              </thead>
              <tbody>
            `;
            datosAseguradora.forEach((record, index) => {
              html += `
                <tr style="background-color: ${index % 2 === 0 ? 'white' : '#f9f9f9'};">
                  <td style="border: 1px solid #ddd; padding: 6px;">${record.aseguradora_nombre || '-'}</td>
                  <td style="border: 1px solid #ddd; padding: 6px; text-align: right;">${record.total_servicios || 0}</td>
                  <td style="border: 1px solid #ddd; padding: 6px; text-align: right;">S/ ${parseFloat(record.total_ingresos || 0).toFixed(2)}</td>
                  <td style="border: 1px solid #ddd; padding: 6px; text-align: right;">${record.total_clientes || 0}</td>
                </tr>
              `;
            });
            html += '</tbody>';
          }
          break;

        case 'unidades':
          if (!datosUnidades || datosUnidades.length === 0) {
            html += '<tr><td colspan="6" style="text-align: center; padding: 20px;">No hay datos para mostrar</td></tr>';
          } else {
            html += `
              <thead>
                <tr style="background-color: #f0f0f0;">
                  <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Placa</th>
                  <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Tipo de Grúa</th>
                  <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Marca/Modelo</th>
                  <th style="border: 1px solid #ddd; padding: 8px; text-align: right;">Total Servicios</th>
                  <th style="border: 1px solid #ddd; padding: 8px; text-align: right;">Total Ingresos</th>
                  <th style="border: 1px solid #ddd; padding: 8px; text-align: right;">Promedio Costo</th>
                  <th style="border: 1px solid #ddd; padding: 8px; text-align: right;">Total Operarios</th>
                </tr>
              </thead>
              <tbody>
            `;
            datosUnidades.forEach((record, index) => {
              const marca = record.marca || '';
              const modelo = record.modelo || '';
              const marcaModelo = [marca, modelo].filter(Boolean).join(' ') || '-';
              html += `
                <tr style="background-color: ${index % 2 === 0 ? 'white' : '#f9f9f9'};">
                  <td style="border: 1px solid #ddd; padding: 6px;">${record.placa || '-'}</td>
                  <td style="border: 1px solid #ddd; padding: 6px;">${record.tipo_grua || '-'}</td>
                  <td style="border: 1px solid #ddd; padding: 6px;">${marcaModelo}</td>
                  <td style="border: 1px solid #ddd; padding: 6px; text-align: right;">${record.total_servicios || 0}</td>
                  <td style="border: 1px solid #ddd; padding: 6px; text-align: right;">S/ ${parseFloat(record.total_ingresos || 0).toFixed(2)}</td>
                  <td style="border: 1px solid #ddd; padding: 6px; text-align: right;">S/ ${parseFloat(record.promedio_costo || 0).toFixed(2)}</td>
                  <td style="border: 1px solid #ddd; padding: 6px; text-align: right;">${record.total_operarios || 0}</td>
                </tr>
              `;
            });
            html += '</tbody>';
          }
          break;

        default:
          html += '<tr><td style="text-align: center; padding: 20px;">No hay datos para mostrar</td></tr>';
      }

      html += `
        </table>
        <div style="margin-top: 20px; text-align: right; font-size: 10px; color: #666;">
          <p>Total de registros: ${tipoReporte === 'servicios' ? (datosServicios?.length || 0) :
          tipoReporte === 'ingresos' ? (datosIngresos?.length || 0) :
            tipoReporte === 'personal' ? (datosTecnicos?.length || 0) :
              tipoReporte === 'aseguradora' ? (datosAseguradora?.length || 0) :
                tipoReporte === 'unidades' ? (datosUnidades?.length || 0) :
                  tipoReporte === 'unidades_aseguradora' ? (datosUnidadesAseguradora?.length || 0) :
                    tipoReporte === 'personal_aseguradora' ? (datosPersonalAseguradora?.length || 0) : 0
        }</p>
        </div>
      `;

      div.innerHTML = html;
      document.body.appendChild(div);

      await new Promise((resolve) => setTimeout(resolve, 500));

      const canvas = await html2canvas(div, {
        scale: 2,
        useCORS: true,
        logging: false,
        windowWidth: div.scrollWidth,
        windowHeight: div.scrollHeight,
      });

      document.body.removeChild(div);

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210;
      const pageHeight = 297;
      const margin = 10;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= (pageHeight - margin);

      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= (pageHeight - margin);
      }

      const fileName = `Reporte_${getTipoReporteNombre().replace(/\s+/g, '_')}_${dayjs().format('YYYYMMDD_HHmmss')}.pdf`;
      pdf.save(fileName);

      message.success({ content: 'PDF generado exitosamente', key: 'export' });
    } catch (error) {
      console.error('Error al generar PDF:', error);
      message.error({ content: 'Error al generar PDF', key: 'export' });
    }
  };

  return (
    <div>
      <Card
        title="Reportes"
        extra={
          <Space>
            <Button
              icon={<FilePdfOutlined />}
              onClick={handleExportarPDF}
              disabled={loading || (
                tipoReporte === 'servicios' && (!datosServicios || datosServicios.length === 0) ||
                tipoReporte === 'ingresos' && (!datosIngresos || datosIngresos.length === 0) ||
                tipoReporte === 'personal' && (!datosTecnicos || datosTecnicos.length === 0) ||
                tipoReporte === 'aseguradora' && (!datosAseguradora || datosAseguradora.length === 0) ||
                tipoReporte === 'unidades' && (!datosUnidades || datosUnidades.length === 0) ||
                tipoReporte === 'unidades_aseguradora' && (!datosUnidadesAseguradora || datosUnidadesAseguradora.length === 0) ||
                tipoReporte === 'personal_aseguradora' && (!datosPersonalAseguradora || datosPersonalAseguradora.length === 0)
              )}
            >
              Exportar PDF
            </Button>
            <Button
              icon={<FilePdfOutlined />} // Using same icon or could import another like FileExcelOutlined if available
              onClick={handleExportarExcel}
              disabled={loading || (
                tipoReporte === 'servicios' && (!datosServicios || datosServicios.length === 0) ||
                tipoReporte === 'ingresos' && (!datosIngresos || datosIngresos.length === 0) ||
                tipoReporte === 'personal' && (!datosTecnicos || datosTecnicos.length === 0) ||
                tipoReporte === 'aseguradora' && (!datosAseguradora || datosAseguradora.length === 0) ||
                tipoReporte === 'unidades' && (!datosUnidades || datosUnidades.length === 0) ||
                tipoReporte === 'unidades_aseguradora' && (!datosUnidadesAseguradora || datosUnidadesAseguradora.length === 0) ||
                tipoReporte === 'personal_aseguradora' && (!datosPersonalAseguradora || datosPersonalAseguradora.length === 0)
              )}
            >
              Exportar Excel
            </Button>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={cargarReporte}
              loading={loading}
            >
              Generar Reporte
            </Button>
          </Space>
        }
      >
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={6}>
            <Select
              value={tipoReporte}
              onChange={setTipoReporte}
              style={{ width: '100%' }}
            >
              <Select.Option value="servicios">Servicios por Fecha</Select.Option>
              <Select.Option value="ingresos">Ingresos por Fecha</Select.Option>
              <Select.Option value="personal">Servicios por Personal</Select.Option>
              <Select.Option value="aseguradora">Servicios por Aseguradora</Select.Option>
              <Select.Option value="unidades">Servicios por Unidades</Select.Option>
              <Select.Option value="unidades_aseguradora">Unidades por Aseguradora</Select.Option>
              <Select.Option value="personal_aseguradora">Personal por Aseguradora</Select.Option>
            </Select>
          </Col>
          {isMainAdmin && (
            <Col xs={24} sm={12} md={6}>
              <Select
                value={empresaId}
                onChange={setEmpresaId}
                style={{ width: '100%' }}
                placeholder="Filtrar por empresa"
              >
                <Select.Option value="all">Todas las empresas</Select.Option>
                {empresas.map((empresa) => (
                  <Select.Option key={empresa.id} value={empresa.id}>
                    {empresa.nombre}
                  </Select.Option>
                ))}
              </Select>
            </Col>
          )}
          <Col xs={24} sm={12} md={isMainAdmin ? 12 : 18}>
            <RangePicker
              value={[fechaInicio, fechaFin]}
              onChange={(dates) => {
                if (dates) {
                  setFechaInicio(dates[0]);
                  setFechaFin(dates[1]);
                }
              }}
              style={{ width: '100%' }}
            />
          </Col>
        </Row>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 50 }}>
            <Spin size="large" />
          </div>
        ) : (
          <>
            {tipoReporte === 'servicios' && (
              <>
                <Table
                  columns={columnsServicios}
                  dataSource={datosServicios}
                  rowKey="id"
                  style={{ marginBottom: 24 }}
                />
              </>
            )}

            {tipoReporte === 'ingresos' && (
              <>
                <Card title="Gráfico de Ingresos" style={{ marginBottom: 24 }}>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={datosIngresos}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="fecha" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="total"
                        stroke="#8884d8"
                        name="Ingresos (S/)"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Card>
                <Table
                  columns={columnsIngresos}
                  dataSource={datosIngresos}
                  rowKey="fecha"
                  style={{ marginBottom: 24 }}
                />
              </>
            )}

            {tipoReporte === 'personal' && (
              <>
                <Card title="Gráfico de Servicios por Personal" style={{ marginBottom: 24 }}>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={datosTecnicos}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="nombre_completo" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="total_servicios" fill="#8884d8" name="Total Servicios" />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
                <Table
                  columns={columnsTecnicos}
                  dataSource={datosTecnicos}
                  rowKey="nombre_completo"
                />
              </>
            )}

            {tipoReporte === 'aseguradora' && (
              <>
                <Card title="Gráfico de Servicios por Aseguradora" style={{ marginBottom: 24 }}>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={datosAseguradora}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="aseguradora_nombre" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="total_servicios" fill="#82ca9d" name="Total Servicios" />
                      <Bar dataKey="total_ingresos" fill="#8884d8" name="Total Ingresos (S/)" />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
                <Table
                  columns={columnsAseguradora}
                  dataSource={datosAseguradora}
                  rowKey="id"
                />
              </>
            )}

            {tipoReporte === 'unidades' && (
              <>
                <Card title="Gráfico de Servicios por Unidades" style={{ marginBottom: 24 }}>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={datosUnidades}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="placa" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="total_servicios" fill="#ffc658" name="Total Servicios" />
                      <Bar dataKey="total_ingresos" fill="#8884d8" name="Total Ingresos (S/)" />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
                <Table
                  columns={columnsUnidades}
                  dataSource={datosUnidades}
                  rowKey="id"
                />
              </>
            )}

            {tipoReporte === 'unidades_aseguradora' && (
              <>
                <Card title="Gráfico de Unidades por Aseguradora" style={{ marginBottom: 24 }}>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={datosUnidadesAseguradora}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="placa" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="total_servicios" fill="#ffc658" name="Total Servicios" />
                      <Bar dataKey="total_ingresos" fill="#8884d8" name="Total Ingresos (S/)" />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
                <Table
                  columns={columnsUnidadesAseguradora}
                  dataSource={datosUnidadesAseguradora}
                  rowKey={(record) => `${record.placa}-${record.aseguradora_nombre}`}
                />
              </>
            )}

            {tipoReporte === 'personal_aseguradora' && (
              <>
                <Card title="Gráfico de Personal por Aseguradora" style={{ marginBottom: 24 }}>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={datosPersonalAseguradora}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="nombre_completo" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="total_servicios" fill="#82ca9d" name="Total Servicios" />
                      <Bar dataKey="total_ingresos" fill="#8884d8" name="Total Ingresos (S/)" />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
                <Table
                  columns={columnsPersonalAseguradora}
                  dataSource={datosPersonalAseguradora}
                  rowKey={(record) => `${record.nombre_completo}-${record.aseguradora_nombre}`}
                />
              </>
            )}
          </>
        )}
      </Card>

      <Modal
        title={tituloDetalle}
        open={modalDetalleVisible}
        onCancel={() => setModalDetalleVisible(false)}
        width={1000}
        footer={[
          <Button key="excel" icon={<FilePdfOutlined />} onClick={handleExportarDetalleExcel}>
            Excel
          </Button>,
          <Button key="pdf" icon={<FilePdfOutlined />} onClick={handleExportarDetallePDF2}>
            PDF
          </Button>,
          <Button key="close" onClick={() => setModalDetalleVisible(false)}>
            Cerrar
          </Button>
        ]}
      >
        <Table
          dataSource={datosDetalle}
          rowKey="id"
          loading={loadingDetalle}
          pagination={{ pageSize: 10 }}
          scroll={{ x: 800 }}
          columns={[
            {
              title: 'Fecha',
              dataIndex: 'fecha_solicitud',
              key: 'fecha_solicitud',
              render: (fecha) => fecha ? dayjs(fecha).format('DD/MM/YYYY HH:mm') : '-',
              width: 140,
            },
            {
              title: 'Cliente',
              key: 'cliente',
              render: (_, record) => `${record.cliente_nombre || ''} ${record.cliente_apellido || ''}`,
              width: 150,
            },
            {
              title: 'Vehículo',
              key: 'vehiculo',
              render: (_, record) => `${record.marca_vehiculo || ''} ${record.modelo_vehiculo || ''} ${record.placa_vehiculo || ''}`,
              width: 150,
            },
            {
              title: 'Servicio',
              key: 'servicio',
              render: (_, record) => record.tipo_servicio_codigo ? `${record.tipo_servicio_codigo} - ${record.tipo_servicio}` : record.tipo_servicio,
              width: 200,
            },
            {
              title: 'Personal',
              dataIndex: 'personal_nombre',
              key: 'personal_nombre',
              width: 150,
            },
            {
              title: 'Unidad',
              dataIndex: 'unidad_placa',
              key: 'unidad_placa',
              width: 100,
            },
            {
              title: 'Costo',
              dataIndex: 'costo',
              key: 'costo',
              render: (costo) => `S/ ${parseFloat(costo || 0).toFixed(2)}`,
              width: 100,
              align: 'right',
            }
          ]}
          summary={(pageData) => {
            let totalCosto = 0;
            pageData.forEach(({ costo }) => {
              totalCosto += parseFloat(costo || 0);
            });
            return (
              <Table.Summary fixed>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={6} align="right">
                    <strong>Total:</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="right">
                    <strong>S/ {totalCosto.toFixed(2)}</strong>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            );
          }}
        />
      </Modal>
    </div>
  );
};

export default Reportes;

